import { MODAL_DIALOG_ID, MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { useModalState } from 'modules/core/hooks';
import { FormRow, MoneyLabel } from 'modules/core/components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import { useTranslation } from 'react-i18next';
import { IRACAffordabilityEngineForm } from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import {
  APPROVED_LOAN_TERMS_FIELD_NAMES,
  FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES,
  FRONT_AND_BACK_ENDS_ADDS_INDEX_FIELD,
} from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { useSelector } from 'react-redux';
import { getRegionControls } from 'store/selectors/RACAffordabilityEngine.selector';
import { IGetVscAndGapLimit, IGetVscAndGapLimitResult } from 'modules/core/types';
import { getCurrentLenderControls } from 'store/selectors/lenders.selector';
import { costFormatter } from 'modules/core/utils';
import { getLenderState } from 'store/selectors/lenders.selector';

import { FRONT_AND_BACK_ENDS_ADDS_FIELDS } from './FrontAndBackEndsAdds.const';
import { TotalNetDownPaymentPanel } from './styles';
import { getFrontAndBackEndsAdds } from './service';
import { FormControlFeedback } from '../LoanVariablesDialog/styles';
import { Panel } from '../commonStyles';
import { getNormalizedDirty } from '../utils';

const { Body } = Card;

interface IFrontAndBackEndsAddsModal {
  onClose: VoidFunction;
  isLender?: boolean;
}

export const FrontAndBackEndsAddsModal: FC<IFrontAndBackEndsAddsModal> = ({ onClose, isLender = false }) => {
  const { t } = useTranslation();
  const regionControls = useSelector(getRegionControls);
  const lenderControls = useSelector(getCurrentLenderControls);
  const lenderState = useSelector(getLenderState);
  const dealSourceData = lenderState?.dealSourceData;
  const { setValue: commonSetValue, getValues: commonGetValues } = useFormContext<IRACAffordabilityEngineForm>();
  const [isVscInvalid, setIsVscInvalid] = useState(false);
  const [isGapInvalid, setIsGapInvalid] = useState(false);

  const defaultValues = commonGetValues().totalFrontAndBackEndsAddsDialog;
  const defaultFormValues = { totalFrontAndBackEndsAddsDialog: defaultValues };
  const formMethods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: defaultFormValues,
  });

  const { reset, watch, getValues, setValue } = formMethods;

  const { isModalDialogActive, handleCloseDialog } = useModalState(
    [MODAL_DIALOG_ID.FRONT_AND_BACK_ENDS_ADDS],
    getValues(),
    reset,
  );

  const normalizedDirty = getNormalizedDirty(formMethods, defaultFormValues);

  const formValues = watch() as IRACAffordabilityEngineForm;

  const { addOns, vsc, gap, totalFrontAndBackEndsAdds } = formValues.totalFrontAndBackEndsAddsDialog;

  useEffect(() => {
    const frontAndBackEndsAdds = getFrontAndBackEndsAdds(addOns || 0, vsc || 0, gap || 0);

    setValue(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.TOTAL_FRONT_AND_BACK_ENDS_ADDS, frontAndBackEndsAdds);
  }, [addOns, vsc, setValue, gap]);

  const handleSubmitModal = useCallback(() => {
    if (!isVscInvalid && !isGapInvalid) {
      handleCloseDialog()();
      commonSetValue(FRONT_AND_BACK_ENDS_ADDS_INDEX_FIELD, getValues().totalFrontAndBackEndsAddsDialog);
      commonSetValue(APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, totalFrontAndBackEndsAdds || 0);
      onClose();
    }
  }, [isVscInvalid, isGapInvalid, handleCloseDialog, commonSetValue, totalFrontAndBackEndsAdds, onClose, getValues]);

  const getVscLimit = useCallback(
    (isLender: boolean, dealSourceData: any, lenderControls: any, regionControls: any): number => {
      if (!isLender) {
        return regionControls.vscLimitBelow;
      }

      if (
        dealSourceData?.finance_details?.override_vsc > 0 &&
        dealSourceData?.finance_details?.override_vsc < lenderControls.maxVSC
      ) {
        return dealSourceData.finance_details.override_vsc;
      }

      return lenderControls.maxVSC;
    },
    [],
  );

  const getVscOrGapLimit = useCallback(
    ({
      fieldName,
      vsc,
      gap,
      t,
      isVscInvalid,
      isGapInvalid,
      regionControls,
      lenderControls,
      setIsVscInvalid,
      setIsGapInvalid,
    }: IGetVscAndGapLimit): IGetVscAndGapLimitResult => {
      let message = '';
      let maxValue = 0;
      let isInvalid = false;

      const vscLimitBelow = getVscLimit(isLender, dealSourceData, lenderControls, regionControls);
      const gapLimit = isLender ? lenderControls.maxGap : regionControls.gapLimit;

      if (fieldName === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC && !!vscLimitBelow) {
        maxValue = vscLimitBelow;
        isInvalid = vsc > vscLimitBelow;

        if (isInvalid !== isVscInvalid) setIsVscInvalid(isInvalid);

        if ((isVscInvalid && isLender) || !isLender) {
          if (isLender) {
            message = t('components.RACAffordabilityEngine.vscAndGapLimits.backEndLimitMessage', {
              maxBackEnd: costFormatter(maxValue, false),
            });
          } else {
            message = t('components.RACAffordabilityEngine.vscAndGapLimits.vscLimitMessage', {
              maxVSC: costFormatter(maxValue, false),
            });
          }
        }
      }

      if (fieldName === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP && !!gapLimit) {
        maxValue = gapLimit;
        isInvalid = gap > maxValue;

        if (isInvalid !== isGapInvalid) setIsGapInvalid(isInvalid);
        message = t('components.RACAffordabilityEngine.vscAndGapLimits.gapLimitMessage', {
          maxGap: costFormatter(maxValue, false),
        });
      }

      return { message, isInvalid };
    },
    [getVscLimit, isLender, dealSourceData],
  );

  const isDisabled = useCallback(
    (name) => {
      if (name === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP) {
        return isLender ? !lenderControls.maxGap : !regionControls.gapLimit;
      }

      if (name === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC) {
        return isLender ? !lenderControls.maxVSC : false;
      }

      return false;
    },
    [isLender, lenderControls.maxGap, lenderControls.maxVSC, regionControls.gapLimit],
  );

  const frontAndBackEndsAddsFields = useMemo(
    () =>
      FRONT_AND_BACK_ENDS_ADDS_FIELDS.filter(
        ({ name }) => name !== FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP || !isDisabled(name),
      ).map(({ name, label, component: Component, controlProps }) => {
        const isVscOrGap = [
          FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC,
          FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP,
        ].includes(name);
        let message = '';
        let isInvalid = false;

        if (isVscOrGap) {
          const { message: vscOrGapMessage, isInvalid: isVscOrGapInvalid } = getVscOrGapLimit({
            fieldName: name,
            vsc,
            gap,
            t,
            isVscInvalid,
            isGapInvalid,
            regionControls,
            lenderControls,
            setIsVscInvalid,
            setIsGapInvalid,
          });

          message = vscOrGapMessage;
          isInvalid = isVscOrGapInvalid;
        }

        if (isLender && name === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC) {
          label = t('components.frontAndBackEndsAddsModal.backEnd');
        }

        return (
          <FormRow
            inputPaddingLeft={0}
            key={name}
            label={label}
            control={
              <>
                <Component name={name} disabled={isDisabled(name)} isInvalid={isInvalid} {...controlProps} />
                {isVscOrGap && (
                  <FormControlFeedback type="invalid" className="d-block" $isInvalid={isInvalid}>
                    {message}
                  </FormControlFeedback>
                )}
              </>
            }
          />
        );
      }),
    [isLender, isDisabled, getVscOrGapLimit, vsc, gap, t, isVscInvalid, isGapInvalid, regionControls, lenderControls],
  );

  return (
    <ModalDialog
      title={t('components.frontAndBackEndsAddsModal.title')}
      submitButtonTitle={t('common.buttons.save')}
      cancelButtonTitle={t('common.buttons.cancel')}
      show={isModalDialogActive}
      onHide={() => {
        handleCloseDialog(true)();
        onClose();
      }}
      onSubmitModal={handleSubmitModal}
      modalSize={MODAL_DIALOG_WIDTH.SM}
      isDisabled={isVscInvalid || isGapInvalid}
      upperCaseTitle
      isDirty={normalizedDirty}
    >
      <Body>
        <FormProvider {...formMethods}>
          <Form>
            <TotalNetDownPaymentPanel>
              <FormRow
                inputPaddingLeft={0}
                label={t('components.frontAndBackEndsAddsModal.totalAdds')}
                control={
                  <MoneyLabel isInteger isColored>
                    {totalFrontAndBackEndsAdds || 0}
                  </MoneyLabel>
                }
              />
            </TotalNetDownPaymentPanel>
            <Panel>{frontAndBackEndsAddsFields}</Panel>
          </Form>
        </FormProvider>
      </Body>
    </ModalDialog>
  );
};
